import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { uniq } from 'lodash';
import { useContext, useState } from 'react';
import { AuthDataProvider } from 'src/components/authProvider';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { PublicationModal } from 'src/components/modal';
import { useGetLocations, useModal } from 'src/hooks';
import { lang } from 'src/utils';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const MultipleSelect = <T extends Array<string | number>>(props: {
  label: string;
  data: T;
  value: T;
  onChange: (value: T) => void;
}) => {
  const handleChange = (event: SelectChangeEvent<T>) => {
    const {
      target: { value },
    } = event;
    props.onChange(typeof value === 'string' ? (value.split(',') as T) : value);
  };

  return (
    <FormControl sx={{ width: 300 }}>
      <InputLabel>{props.label}</InputLabel>
      <Select
        multiple
        value={props.value}
        onChange={handleChange}
        input={<OutlinedInput label={props.label} />}
        MenuProps={MenuProps}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((value) => (
              <DynamicTooltipChip key={value} chipName={`${value}`} />
            ))}
          </Box>
        )}>
        {props.data.map((name) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const DevPublications = () => {
  const publishModal = useModal();
  const { userRole } = useContext(AuthDataProvider);
  const { data: locations = [] } = useGetLocations(
    ['sort=*location_code'],
    userRole,
  );

  const [selectedLocationCodes, setSelectedLocationCodes] = useState<string[]>(
    [],
  );
  const [selectedLocationGroupIDs, setSelectedLocationGroupIDs] = useState<
    number[]
  >([]);

  const onPublish = () => {
    publishModal.onOpenModal();
  };

  const locationCodes = locations.map((location) => location.location_code);

  const locationGroupsIDs = uniq(
    locations.flatMap((location) => location.location_group_id),
  );

  return (
    <Stack alignItems="start" gap="10px">
      <Box>
        <MultipleSelect
          label="Location codes"
          data={locationCodes}
          value={selectedLocationCodes}
          onChange={setSelectedLocationCodes}
        />

        <MultipleSelect
          label="Location group IDs"
          data={locationGroupsIDs}
          value={selectedLocationGroupIDs}
          onChange={setSelectedLocationGroupIDs}
        />
      </Box>

      <Button onClick={onPublish}>{lang.publish}</Button>

      {publishModal.open && (
        <PublicationModal
          open={publishModal.open}
          locationGroupIDs={selectedLocationGroupIDs}
          locationCodes={selectedLocationCodes}
          onClose={publishModal.onCloseModal}
        />
      )}
    </Stack>
  );
};
